import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Auth } from 'aws-amplify';
import CONFIG from '../../config/config';
import { Settings } from '../../store/reducers/AccountSlice';
import { isValidEmail } from '../../shared/validation';
import {
  getUnreadCount,
  hideChat,
  identifyVisitor,
  isChatOpened,
  loadCrisp,
  onCrispEvent,
  openChat as openCrispChat,
  resetSession,
  setCrispLocale,
  type CrispReceivedMessage,
} from '../../services/crisp';

type CrispChatContextValue = {
  openChat: () => void;
  unreadCount: number;
  isReady: boolean;
};

const CrispChatContext = createContext<CrispChatContextValue | null>(null);

export function CrispProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  const settings: Settings = useSelector((state: any) => state.settings.value);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const wasLoggedIn = useRef(false);

  useEffect(() => {
    if (!settings.isLoggedIn) {
      setIsReady(false);
      setUnreadCount(0);
      return;
    }

    loadCrisp(CONFIG.CRISP_WEBSITE_ID);
    hideChat();
    setCrispLocale(i18n.language);

    const unsubLoaded = onCrispEvent('session:loaded', () => {
      setIsReady(true);
      const seededUnread = getUnreadCount();
      if (!isChatOpened()) {
        hideChat();
        setUnreadCount(seededUnread);
      } else {
        setUnreadCount(0);
      }
    });
    const unsubReceived = onCrispEvent('message:received', (payload: unknown) => {
      const message = payload as CrispReceivedMessage | undefined;
      if (message?.origin === 'update' || isChatOpened()) return;
      setUnreadCount((prev) => Math.max(prev + 1, getUnreadCount()));
    });
    const unsubOpened = onCrispEvent('chat:opened', () => {
      setUnreadCount(0);
    });
    const unsubClosed = onCrispEvent('chat:closed', () => {
      hideChat();
    });

    return () => {
      unsubLoaded();
      unsubReceived();
      unsubOpened();
      unsubClosed();
    };
    // Locale is applied in a separate effect; this effect re-runs on login/logout.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.isLoggedIn]);

  useEffect(() => {
    if (!settings.isLoggedIn) return;
    setCrispLocale(i18n.language);
  }, [i18n.language, settings.isLoggedIn]);

  useEffect(() => {
    if (!settings.isLoggedIn) {
      if (wasLoggedIn.current) {
        resetSession();
        hideChat();
      }
      wasLoggedIn.current = false;
      return;
    }

    wasLoggedIn.current = true;
    let cancelled = false;

    const identify = async () => {
      let email: string | undefined;
      try {
        const user = await Auth.currentAuthenticatedUser();
        const candidate = user?.attributes?.email as string | undefined;
        if (candidate && isValidEmail(candidate)) {
          email = candidate.trim();
        }
      } catch (error) {
        console.error('Failed to read Cognito user for Crisp identity', { error });
      }

      if (cancelled) return;

      try {
        identifyVisitor({
          email,
          tenantId: settings.tenantId,
          storeName: settings.tenantName,
          storePlan: settings.pricingPlan,
          storeEmail: settings.storeEmail && isValidEmail(settings.storeEmail)
            ? settings.storeEmail.trim()
            : undefined,
        });
      } catch (error) {
        console.error('Failed to identify Crisp visitor', {
          tenantId: settings.tenantId,
          error,
        });
      }
    };

    identify();
    return () => {
      cancelled = true;
    };
  }, [
    settings.isLoggedIn,
    settings.tenantId,
    settings.tenantName,
    settings.pricingPlan,
    settings.storeEmail,
  ]);

  const openChat = useCallback(() => {
    if (!settings.isLoggedIn) return;
    openCrispChat();
  }, [settings.isLoggedIn]);

  const value = useMemo(
    () => ({ openChat, unreadCount, isReady }),
    [openChat, unreadCount, isReady]
  );

  return (
    <CrispChatContext.Provider value={value}>{children}</CrispChatContext.Provider>
  );
}

export function useCrispChat(): CrispChatContextValue {
  const context = useContext(CrispChatContext);
  if (!context) {
    throw new Error('useCrispChat must be used within CrispProvider');
  }
  return context;
}
